import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { reviewService } from '../../services/reviewService';
import Icon from '../../components/AppIcon';
import Toast from '../../components/ui/Toast';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';

const Reviews = () => {
  const { userProfile } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ avg: 0, total: 0, breakdown: [] });

  useEffect(() => {
    if (userProfile?.id) loadReviews();
  }, [userProfile?.id]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const data = await reviewService.getPhotographerReviews(userProfile.id);
      setReviews(data || []);

      // Calculate stats
      if (data && data.length > 0) {
        const avg = (data.reduce((sum, r) => sum + (r.rating || 0), 0) / data.length).toFixed(1);
        const breakdown = [5, 4, 3, 2, 1].map(stars => ({
          stars,
          count: data.filter(r => Math.round(r.rating) === stars).length,
          percentage: Math.round((data.filter(r => Math.round(r.rating) === stars).length / data.length) * 100)
        }));
        setStats({ avg: parseFloat(avg), total: data.length, breakdown });
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
      setToast({ type: 'error', message: 'فشل تحميل التقييمات' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      calendar: 'gregory'
    });
  };

  const renderStars = (rating, size = 16) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={size}
        className={i < Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}
      />
    ));
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length > 1
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name[0].toUpperCase();
  };

  const getRatingLabel = (rating) => {
    if (rating >= 4.5) return { text: 'ممتاز', color: 'text-green-600', bg: 'bg-green-50' };
    if (rating >= 3.5) return { text: 'جيد جداً', color: 'text-blue-600', bg: 'bg-blue-50' };
    if (rating >= 2.5) return { text: 'جيد', color: 'text-amber-600', bg: 'bg-amber-50' };
    if (rating >= 1.5) return { text: 'مقبول', color: 'text-orange-600', bg: 'bg-orange-50' };
    return { text: 'يحتاج تحسين', color: 'text-red-600', bg: 'bg-red-50' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Icon name="Star" size={28} className="text-amber-500 fill-amber-500" />
          تقييمات العملاء
        </h1>
        <p className="text-slate-500 mt-1">تقييمات العملاء لجلسات التصوير الخاصة بك</p>
      </div>

      {reviews.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <Icon name="Star" size={64} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">لا توجد تقييمات بعد</h3>
          <p className="text-slate-500">ستظهر تقييمات العملاء هنا بعد تسليم الصور لهم</p>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Average Rating */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
              <div className="text-5xl font-bold text-slate-900 mb-2">{stats.avg}</div>
              <div className="flex justify-center gap-1 mb-2">
                {renderStars(stats.avg, 20)}
              </div>
              <p className="text-sm text-slate-500">التقييم العام</p>
              <div className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${getRatingLabel(stats.avg).bg} ${getRatingLabel(stats.avg).color}`}>
                {getRatingLabel(stats.avg).text}
              </div>
            </div>

            {/* Total Reviews */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
              <div className="text-5xl font-bold text-slate-900 mb-2">{stats.total}</div>
              <div className="flex justify-center mb-2">
                <Icon name="MessageSquare" size={24} className="text-primary" />
              </div>
              <p className="text-sm text-slate-500">إجمالي التقييمات</p>
            </div>

            {/* Rating Breakdown */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <p className="text-sm font-semibold text-slate-700 mb-3 text-center">توزيع التقييمات</p>
              <div className="space-y-2">
                {stats.breakdown.map(item => (
                  <div key={item.stars} className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-6 text-left">{item.stars}</span>
                    <Icon name="Star" size={12} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 rounded-full transition-all"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-500 w-6">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-800">
              جميع التقييمات ({reviews.length})
            </h2>

            {reviews.map((review) => {
              const customerName = review.customer?.fullName || review.customer?.full_name || 'عميل';
              const customerImage = review.customer?.avatarUrl || review.customer?.avatar_url || null;
              const photographyType = review.photographyType || review.photography_type || null;

              return (
                <div
                  key={review.id}
                  className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    {/* Customer Avatar */}
                    {customerImage ? (
                      <img
                        src={customerImage}
                        alt={customerName}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-slate-100"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0 border-2 border-primary/20">
                        <span className="text-primary font-bold text-sm">
                          {getInitials(customerName)}
                        </span>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      {/* Header Row */}
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <h4 className="font-semibold text-slate-900">{customerName}</h4>
                          <p className="text-xs text-slate-500">
                            {formatDate(review.createdAt || review.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {renderStars(review.rating)}
                        </div>
                      </div>

                      {/* Comment */}
                      {(review.comment) && (
                        <p className="text-sm text-slate-700 leading-relaxed mb-3">
                          {review.comment}
                        </p>
                      )}

                      {/* Photography Type Badge */}
                      {photographyType && (
                        <div className="flex items-center gap-2">
                          <Icon name="Camera" size={14} className="text-slate-400" />
                          <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-lg">
                            {getPhotographyTypeLabelAr(photographyType)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Reviews;
